import { getMeetingTokens } from '../infrastructure/api-hca'
import { useCallback, useState } from 'react'
import { useTimeline } from './useTimeline'
import { useTranslation } from 'react-i18next'
import { useUIContext } from '../context/UIContext'
import { useUserCourses } from './queries/useUserCourses'
import { useToken } from './useToken'
import { useQueryClient } from '@tanstack/react-query'

export const useOpenMeet = (courseId?: string) => {
    const { _id } = useToken()
    const queryClient = useQueryClient()
    const { t } = useTranslation('dashboard')
    const { showToast, showError } = useUIContext()
    const { currentEvent, invalidateTimeLine } = useTimeline(courseId)
    const { getCourseData } = useUserCourses()
    const course = getCourseData(courseId)
    const [isOpening, setIsOpening] = useState(false)

    const openMeet = useCallback(async (): Promise<Window | null> => {
        const toastId = 'open-meet-toast' as const
        try {
            showToast(t('Opening live session...'), { id: toastId })
            setIsOpening(true)

            if (!course?.meetings || !courseId) throw new Error('Course ID and meetings are required')
            const { algorithmId, meetings } = course

            let meetUrl: string | null = null

            // El timeline es una PISTA, no una decisión: lo único que puede hacer es mandarnos a la
            // mesa. Cualquier otro estado (general, room null tras "Finalizar reuniones", streaming,
            // sesión sin evento todavía, cache vieja) entra por la general, que es el camino robusto.
            // Si la sala no era la correcta, la app de meet reubica al alumno.
            const wantsTeams = currentEvent?.room === 'teams'
            const provider = wantsTeams ? meetings.team?.provider : meetings.general?.provider

            switch (provider) {
                case 'egg':
                case 'zoom-hybrid':
                case undefined: // This handle some special cases where the provider is not defined
                    const { general_room, teams_room } = await getMeetingTokens(algorithmId)
                    // El fallback compara `.url`, NO el objeto de sala: HCA manda `{ url: null }`
                    // cuando la sala todavía no existe, y un objeto siempre es truthy — por eso el
                    // `general_room ?? teams_room` anterior era código muerto.
                    // El `?? teams_room?.url` final rescata al curso que solo tiene mesas.
                    meetUrl = (wantsTeams ? teams_room?.url : null) ?? general_room?.url ?? teams_room?.url ?? null
                    break

                default:
                    meetUrl = meetings.general?.url ?? null
                    break
            }

            if (!meetUrl) {
                // HCA responde 202 cuando ninguna de las dos salas tiene url: la clase existe pero
                // todavía no hay a dónde entrar. Es una espera, no un error del alumno.
                showToast(t('The meeting rooms are not ready yet, please try again in a moment'), { id: toastId })
                return null
            }

            const meetWindow = window.open(meetUrl, '_blank')
            if (meetWindow === null) {
                showError(t('Please allow pop-ups for this site to join the meeting'), { id: toastId })
                return null
            }
            meetWindow.focus()
            return meetWindow
        } catch (error) {
            invalidateTimeLine()
            queryClient.invalidateQueries({
                queryKey: ['User', _id],
                exact: false,
            })
            showError(t('An error occurred while trying to open the meeting'), { id: toastId })
            console.error(error)
            return null
        } finally {
            setIsOpening(false)
        }
    }, [showToast, t, course, courseId, currentEvent, showError, invalidateTimeLine, queryClient, _id])

    return { openMeet, isOpening }
}
